import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  AlertColor,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import PageTitle from "../../molecules/PageTitle";
import { useNavigate, useParams } from "react-router-dom";
import OwnerRegistrationModal from "./OwnerRegistrationModal";
import { OwnerSelectionModal } from "./OwnerSelectionModal";
import { BuildingEditFrom } from "./BuildingEditForm";
import { BuildingEditOwnerDetails } from "./BuildingEditOwnerDetails";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { CustomContainer } from "../../../styles/styles";
import ConfirmDialog from "../../molecules/ConfirmDialog";
import NavigateButton from "../../atoms/Button/NavigateButton";
import { SdcReplacement, SdcReplacementTable } from "./SdcReplacementsTable";
import { BuildingSdcsTable, Sdc } from "./BuildingSdcsTable";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  ErrorWithRetry,
  LoadingSpinner,
} from "../../molecules/LoadingAndError";

export type Building = {
  autoBuildingId: number;
  buildingId: string;
  buildingManagementId: string;
  buildingName: string;
  buildingAddress: string;
  createdAt: string;
  memo: string;
  isConnectionAlertEnabled: boolean;
  connectionAlertEmail: string;
  connectionRecoveryAlertPeriod: number;
  connectionAlertPeriod: number;
  connectionReminderAlertPeriod: number;
  isAnomalyAlertEnabled: boolean;
  anomalyAlertEmail: string;

  isAnomalyUnresponsiveOpenAlertEnabled: boolean;
  isAnomalyUnresponsiveLockAlertEnabled: boolean;
  fulltechOffice: {
    autoFulltechOfficeId: number;
    officeName: string;
  };
  owner: Owner;
  sdcs: Sdc[];
};

export type Owner = {
  autoOwnerId: number | undefined;
  ownerId: string;
  password: string;
  subscribeNumber: string;
  ownerPhone: string;
  ownerEmail: string;
  ownerName: string;
  departmentName: string;
  representativeName: string;
  picName: string;
  isEnabled: boolean;
  memo: string | undefined;
  createdAt: string;
};

const BuildingEditPage: React.FC = () => {
  const { autoBuildingId } = useParams();

  const [building, setBuilding] = useState<Building | undefined>(undefined);
  const [buildingId, setBuildingId] = useState("");
  const [buildingManagementId, setBuildingManagementId] = useState("");
  const [buildingName, setBuildingName] = useState("");
  const [buildingAddress, setBuildingAddress] = useState("");
  const [createdAt, setCreatedAt] = useState();
  const [memo, setMemo] = useState("");
  const [isConnectionAlertEnabled, setIsConnectionAlertEnabled] =
    useState(false);
  const [connectionAlertEmail, setConnectionAlertEmail] = useState("");
  const [connectionRecoveryAlertPeriod, setConnectionRecoveryAlertPeriod] =
    useState(0);
  const [connectionAlertPeriod, setConnectionAlertPeriod] = useState(0);
  const [connectionReminderAlertPeriod, setConnectionReminderAlertPeriod] =
    useState(0);
  const [isAnomalyAlertEnabled, setIsAnomalyAlertEnabled] = useState(false);
  const [anomalyAlertEmail, setAnomalyAlertEmail] = useState("");
  const [
    isAnomalyUnresponsiveOpenAlertEnabled,
    setIsAnomalyUnresponsiveOpenAlertEnabled,
  ] = useState(false);
  const [
    isAnomalyUnresponsiveLockAlertEnabled,
    setIsAnomalyUnresponsiveLockAlertEnabled,
  ] = useState(false);
  const [isDoorStackedAlertEnabled, setIsDoorStackedAlertEnabled] =
    useState(false); // 20001
  const [isCannotAllCloseAlertEnabled, setIsCannotAllCloseAlertEnabled] =
    useState(false); // 20002
  const [
    isCannotAnswerBackOnAlertEnabled,
    setIsCannotAnswerBackOnAlertEnabled,
  ] = useState(false); // 20003
  const [
    isCannotAnswerBackOffAlertEnabled,
    setIsCannotAnswerBackOffAlertEnabled,
  ] = useState(false); // 20004

  const [fulltechOfficeName, setFulltechOfficeName] = useState<any>();
  const [fulltechOfficeId, setFulltechOfficeId] = useState<any>();
  const [sdcs, setSdcs] = useState<Sdc[]>([]);

  const [isSaving, setIsSaving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // sdc replacements
  const [sdcReplacements, setSdcReplacements] = useState<SdcReplacement[]>([]);

  const [baseModalOpen, setBaseModalOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<Owner | undefined>(
    undefined
  );
  const [openOwnerModal, setOpenOwnerModal] = useState(false);

  const navigate = useNavigate();

  const [openOwnerRegistrationModal, setOpenOwnerRegistrationModal] =
    useState(false);

  const [newOwner, setNewOwner] = useState<Owner>({
    autoOwnerId: undefined,
    ownerId: "",
    password: "",
    subscribeNumber: "",
    ownerPhone: "",
    ownerEmail: "",
    ownerName: "",
    departmentName: "",
    representativeName: "",
    picName: "",
    isEnabled: true,
    memo: undefined,
    createdAt: "",
  });

  // データ取得の状態管理を追加
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // アラート
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");

  const openSnackBarWithSuccess = (message: string) => {
    setSnackbarMessage(message);
    setAlertSeverity("success");
    setSnackbarOpen(true);
  };

  const openSnackBarWithError = (message: string) => {
    setSnackbarMessage(message);
    setAlertSeverity("error");
    setSnackbarOpen(true);
  };

  // 確認ダイアログ
  const [ownerChangeConfirmOpen, setOwnerChangeConfirmOpen] =
    React.useState(false);

  // データ取得
  const fetchBuilding = async () => {
    setIsLoading(true);
    setHasError(false);

    try {
      const jwtToken = await getJwtTokenFromIdToken();
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT + "/buildings/" + autoBuildingId,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setBuilding(data);
      setBuildingId(data.buildingId);
      setBuildingManagementId(data.buildingManagementId);
      setBuildingName(data.buildingName);
      setBuildingAddress(data.buildingAddress);
      setCreatedAt(data.createdAt);
      setMemo(data.memo);
      setIsConnectionAlertEnabled(data.isConnectionAlertEnabled);
      setConnectionAlertEmail(data.connectionAlertEmail);
      setConnectionRecoveryAlertPeriod(data.connectionRecoveryAlertPeriod);
      setConnectionAlertPeriod(data.connectionAlertPeriod);
      setConnectionReminderAlertPeriod(data.connectionReminderAlertPeriod);
      setIsAnomalyAlertEnabled(data.isAnomalyAlertEnabled);
      setAnomalyAlertEmail(data.anomalyAlertEmail);

      setIsAnomalyUnresponsiveOpenAlertEnabled(
        data.isAnomalyUnresponsiveOpenAlertEnabled
      );
      setIsAnomalyUnresponsiveLockAlertEnabled(
        data.isAnomalyUnresponsiveLockAlertEnabled
      );
      setIsDoorStackedAlertEnabled(data.isDoorStackedAlertEnabled);
      setIsCannotAllCloseAlertEnabled(data.isCannotAllCloseAlertEnabled);
      setIsCannotAnswerBackOnAlertEnabled(
        data.isCannotAnswerBackOnAlertEnabled
      );
      setIsCannotAnswerBackOffAlertEnabled(
        data.isCannotAnswerBackOffAlertEnabled
      );
      setFulltechOfficeName(data.fulltechOffice.officeName);
      setFulltechOfficeId(data.fulltechOffice.autoFulltechOfficeId);
      setSdcs(data.sdcs);
      setSdcReplacements(data.sdcReplacements);
      console.log("取得データ" + JSON.stringify(data));
    } catch (error) {
      console.error("Building Fetch Error:", error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBuilding();
  }, []);

  const handleSelectOwner = (
    owner: React.SetStateAction<Owner | undefined>
  ) => {
    setSelectedOwner(owner);
    setOpenOwnerModal(false);
  };

  //  「保存」ボタンのクリックイベントハンドラを定義
  const handleSaveClick = async () => {
    if (!selectedOwner && !building?.owner) {
      openSnackBarWithError("LAD契約者（オーナー）が選択されていません");
      return;
    }
    setOwnerChangeConfirmOpen(true);
  };

  const handleNewDoorsClick = () => {
    navigate(`/buildings/${autoBuildingId}/doors/new`);
    console.log(`SDC（ドア）新規登録 button clicked`);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (hasError) {
    return (
      <ErrorWithRetry
        onRetry={fetchBuilding} // または適切な再取得関数
        // 必要に応じて他のpropsを渡す
        // errorMessage="カスタムエラーメッセージ"
        // minHeight="50vh"
      />
    );
  }

  return (
    <CustomContainer maxWidth={false}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {/* ページタイトルコンポーネント */}
            <PageTitle title="物件詳細・編集" />
            <NavigateButton to={`/buildings`} buttonTitle={"管理物件一覧"} />
          </Box>
        </Grid>
        <Grid item xs={7}>
          <BuildingEditFrom
            building={building}
            buildingManagementId={buildingManagementId}
            buildingName={buildingName}
            buildingAddress={buildingAddress}
            createdAt={createdAt}
            memo={memo}
            fulltechOfficeName={fulltechOfficeName}
            fulltechOfficeId={fulltechOfficeId}
            baseModalOpen={baseModalOpen}
            setBuildingManagementId={setBuildingManagementId}
            setBuildingName={setBuildingName}
            setBuildingAddress={setBuildingAddress}
            setMemo={setMemo}
            isConnectionAlertEnabled={isConnectionAlertEnabled}
            setConnectionAlertEnabled={setIsConnectionAlertEnabled}
            connectionAlertEmail={connectionAlertEmail}
            setConnectionAlertEmail={setConnectionAlertEmail}
            connectionRecoveryAlertPeriod={connectionRecoveryAlertPeriod}
            setConnectionRecoveryAlertPeriod={setConnectionRecoveryAlertPeriod}
            connectionAlertPeriod={connectionAlertPeriod}
            setConnectionAlertPeriod={setConnectionAlertPeriod}
            connectionReminderAlertPeriod={connectionReminderAlertPeriod}
            setConnectionReminderAlertPeriod={setConnectionReminderAlertPeriod}
            isAnomalyAlertEnabled={isAnomalyAlertEnabled}
            setAnomalyAlertEnabled={setIsAnomalyAlertEnabled}
            anomalyAlertEmail={anomalyAlertEmail}
            setAnomalyAlertEmail={setAnomalyAlertEmail}
            isAnomalyUnresponsiveOpenAlertEnabled={
              isAnomalyUnresponsiveOpenAlertEnabled
            }
            setAnomalyUnresponsiveOpenAlertEnabled={
              setIsAnomalyUnresponsiveOpenAlertEnabled
            }
            isAnomalyUnresponsiveLockAlertEnabled={
              isAnomalyUnresponsiveLockAlertEnabled
            }
            setAnomalyUnresponsiveLockAlertEnabled={
              setIsAnomalyUnresponsiveLockAlertEnabled
            }
            isDoorStackedAlertEnabled={isDoorStackedAlertEnabled}
            setIsDoorStackedAlertEnabled={setIsDoorStackedAlertEnabled}
            isCannotAllCloseAlertEnabled={isCannotAllCloseAlertEnabled}
            setIsCannotAllCloseAlertEnabled={setIsCannotAllCloseAlertEnabled}
            isCannotAnswerBackOnAlertEnabled={isCannotAnswerBackOnAlertEnabled}
            setIsCannotAnswerBackOnAlertEnabled={
              setIsCannotAnswerBackOnAlertEnabled
            }
            isCannotAnswerBackOffAlertEnabled={
              isCannotAnswerBackOffAlertEnabled
            }
            setIsCannotAnswerBackOffAlertEnabled={
              setIsCannotAnswerBackOffAlertEnabled
            }
            setFulltechOfficeName={setFulltechOfficeName}
            setFulltechOfficeId={setFulltechOfficeId}
            setBaseModalOpen={setBaseModalOpen}
            buildingId={buildingId}
            setSnackbarOpen={setSnackbarOpen}
            setAlertSeverity={setAlertSeverity}
            setSnackbarMessage={setSnackbarMessage}
          />
        </Grid>

        <Grid item xs={5}>
          <Box display="flex" flexDirection="column" marginBottom={2}>
            <BuildingEditOwnerDetails
              selectedOwner={selectedOwner}
              buildingOwner={building?.owner}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOpenOwnerModal(true)}
            >
              選択
            </Button>
            <Button
              type="submit"
              variant="outlined"
              color="primary"
              onClick={handleSaveClick}
              disabled={isSaving} // 処理中はボタンを無効化
            >
              {isSaving ? <CircularProgress size={24} /> : "保存"}
            </Button>
            {/* <Button variant="outlined" color="primary" onClick={handleNewOwnersClick} sx={{ marginLeft: 1 }}>新規作成</Button> */}{" "}
            {/* ユーザーを困らせないようにここからのLAD契約者（オーナー）作成の動線は消すことになった*/}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginBottom={2}
          >
            <Typography variant="h6">SDC（ドア）一覧</Typography>
            <Box>
              <Button
                variant="outlined"
                color="primary"
                onClick={() =>
                  navigate(`/buildings/${autoBuildingId}/billing-information`)
                }
                sx={{ marginRight: 2 }}
              >
                契約情報一覧
              </Button>
            </Box>
          </Box>
          <BuildingSdcsTable
            sdcs={sdcs}
            navigate={navigate}
            onSuccess={openSnackBarWithSuccess}
            onError={openSnackBarWithError}
          />
          <Box display="flex" justifyContent="flex-end" marginTop={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleNewDoorsClick}
            >
              SDC（ドア）追加
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" marginBottom={2}>
            SDC交換履歴
          </Typography>
          <SdcReplacementTable
            sdcReplacements={sdcReplacements}
            setSdcReplacements={setSdcReplacements}
            onSuccess={openSnackBarWithSuccess}
            onError={openSnackBarWithError}
          />
        </Grid>
      </Grid>

      <OwnerSelectionModal
        openOwnerModal={openOwnerModal}
        setOpenOwnerModal={setOpenOwnerModal}
        handleSelectOwner={handleSelectOwner}
        setSnackbarOpen={setSnackbarOpen}
        setAlertSeverity={setAlertSeverity}
        setSnackbarMessage={setSnackbarMessage}
      />
      <OwnerRegistrationModal
        openOwnerRegistrationModal={openOwnerRegistrationModal}
        setOpenOwnerRegistrationModal={setOpenOwnerRegistrationModal}
        newOwner={newOwner}
        setNewOwner={setNewOwner}
        setSnackbarOpen={setSnackbarOpen}
        setAlertSeverity={setAlertSeverity}
        setSnackbarMessage={setSnackbarMessage}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={ownerChangeConfirmOpen}
        onClose={() => {
          setOwnerChangeConfirmOpen(false);
        }}
        onConfirm={async () => {
          setIsSaving(true); // 処理中フラグを立てる
          // JWTトークンを取得します
          const jwtToken = await getJwtTokenFromIdToken();
          try {
            // call attachOwner API
            const response = await fetch(
              process.env.REACT_APP_API_ENDPOINT +
                `/buildings/${autoBuildingId}/attach-owner`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: jwtToken,
                },
                body: JSON.stringify({
                  autoOwnerId: selectedOwner?.autoOwnerId,
                }),
              }
            );

            if (response.ok) {
              openSnackBarWithSuccess("保存が完了しました。");
            } else {
              openSnackBarWithError("保存に失敗しました。");
            }
          } catch (error) {
            openSnackBarWithError("エラーが発生しました。");
            console.error(error);
          } finally {
            setIsSaving(false); // 処理終了
          }
        }}
        title="確認"
        description="LAD契約者（オーナー）を変更すると、この物件に紐づいているオペレーター情報がリセットされますが、よろしいですか？"
      />
    </CustomContainer>
  );
};

export default BuildingEditPage;
